import nav from './nav.json';
import canon from './genre-canon.json';
import { slugify } from './events';
import type { Event } from '../types';

/**
 * Genre subcategories for Concerts and Theater, from the `genre` each event
 * carries (SeatGeek performer genre, else the partner feeds' category). Labels
 * are tidied for people — SeatGeek says "Rnb", the feeds "R&b / Soul" — and
 * near-duplicates fold together. The map is shared with the build
 * (src/data/genre-canon.json, read by scripts/hubs.mjs), so the menu counts and
 * the genre pages agree. Comedy is one genre, so it stays organised by
 * comedian; Sports by league (src/data/leagues.ts).
 */
const CANON = canon as Record<string, string>;

/** The label a shopper sees for a raw genre value ('' = no genre, e.g. "Other"). */
const key = (raw: string) => raw.trim().toLowerCase().replace(/\s*\/\s*/g, ' / ');
export const genreLabel = (raw: string) => CANON[key(raw)] ?? raw.trim();

export const genreSlug = (raw: string) => slugify(genreLabel(raw).replace('&', ' and '));

export interface GenreSection {
  label: string;
  slug: string;
  n: number;
}

/** Genres with enough upcoming dates to be a page, busiest first. Built from
 *  nav.json so the chrome needs no catalog slice; labels merged by CANON. */
const sections = (category: 'Music' | 'Theater', min = 8): GenreSection[] => {
  const by = new Map<string, GenreSection>();
  for (const g of (nav as { genres: Record<string, { name: string; n: number }[]> }).genres[category] ?? []) {
    const label = genreLabel(g.name);
    if (!label) continue;
    const slug = genreSlug(g.name);
    const have = by.get(slug);
    if (have) have.n += g.n;
    else by.set(slug, { label, slug, n: g.n });
  }
  return [...by.values()].filter((g) => g.n >= min).sort((a, b) => b.n - a.n);
};

export const GENRES: Record<'Music' | 'Theater', GenreSection[]> = {
  Music: sections('Music'),
  Theater: sections('Theater'),
};

export const genreCategoryForPath = (base: string): 'Music' | 'Theater' | null =>
  base === 'music' ? 'Music' : base === 'theater' ? 'Theater' : null;

export const findGenre = (category: 'Music' | 'Theater', slug: string) =>
  GENRES[category].find((g) => g.slug === slug) ?? null;

/** Does this event belong to the genre page? */
export const inGenre = (e: Event, slug: string) => !!e.genre && !!genreLabel(e.genre) && genreSlug(e.genre) === slug;

/** "Country concerts" / "Family shows" — the page heading. */
export const genreHeading = (category: 'Music' | 'Theater', label: string) =>
  category === 'Music'
    ? label === 'Classical'
      ? 'Classical concerts'
      : `${label} concerts`
    : label === 'Las Vegas'
      ? 'Las Vegas shows'
      : label === 'Musicals & plays'
        ? 'Musicals & plays'
        : label === 'Broadway' || label === 'Off-Broadway'
          ? `${label} shows`
          : `${label} shows`;
